<template>
    <div :data-type="type" class="form-item-check-input">
        <div v-if="question.optionHtml === 'default'"
             class="btn-group d-flex"
             :class="[question.optionOrder === 'column' || question.optionOrder === '0' ? 'flex-column' : 'flex-wrap']">
            <div v-for="(item, optionValue) in options" :key="optionValue" class="form-check pe-3">
                <input
                    :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                    :class="[inputClass, { 'is-invalid': shouldShowError }]"
                    :name="name"
                    type="checkbox"
                    :value="optionValue"
                    :checked="isOptionSelected(optionValue)"
                    @change="handleCheckboxChange(optionValue, $event.target.checked)" />

                <label :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue" class="form-check-label" v-html="item.label">
                </label>
                <div
                    v-if="item.additionalInfo != ''"
                    class="form-input-help mb-1"
                    v-html="item.additionalInfo"
                >
                </div>

                <!-- Campo de texto para "other" -->
                <div v-if="optionValue === 'other' && selectedValues.includes('other')" class="mt-2">
                    <input
                        type="text"
                        class="form-control"
                        :class="[{ 'is-invalid': shouldShowError }]"
                        :placeholder="otherText || 'Especificar...'"
                        v-model="otherValue"
                        @input="handleOtherTextChange($event.target.value)" />
                </div>
            </div>
        </div>
        <div class="btn-group d-flex"
             :class="[question.optionOrder === 'column' || question.optionOrder === '0' ? 'flex-column' : 'flex-wrap']"
             v-if="question.optionHtml === 'button'">
            <div v-for="(item, optionValue) in options" :key="optionValue" class="form-check">
                <input
                    :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                    :class="[inputClass, { 'is-invalid': shouldShowError }]"
                    :name="name"
                    type="checkbox"
                    :value="optionValue"
                    :checked="isOptionSelected(optionValue)"
                    @change="handleCheckboxChange(optionValue, $event.target.checked)" />

                <label class="btn btn-outline-primary btn-check-icon-outline label-options" :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue" v-html="item.label">
                </label>

                <div
                    v-if="item.additionalInfo != ''"
                    class="form-input-help mb-1"
                    v-html="item.additionalInfo"
                >
                </div>

                <!-- Campo de texto para "other" -->
                <div v-if="optionValue === 'other' && selectedValues.includes('other')" class="mt-2">
                    <input
                        type="text"
                        class="form-control question-input-text-other"
                        :class="[{ 'is-invalid': shouldShowError }]"
                        :placeholder="otherText || 'Especificar...'"
                        v-model="otherValue"
                        @input="handleOtherTextChange($event.target.value)" />
                </div>
            </div>
        </div>
        <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
            {{ errorMessage }}
        </div>
    </div>
</template>
<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    errorText: { type: String, required: false },
    fieldId: { type: [Number, String], required: true },
    inputClass: { type: String, required: false },
    name: { type: String, required: true },
    options: { type: Object, required: true },
    otherId: { type: Number, required: false },
    otherText: { type: String, required: false },
    question: { type: Object, required: true },
    type: { type: String, required: true },
    typeFieldId: { type: Number, required: true },
    validation: { type: Object, default: () => ({}) },
    hasSubmitted: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelAnswer', 'register-field', 'field-change', 'other-selected']);

// REACTIVE VARIABLES
const selectedValues = ref([]);
const otherValue = ref('');
const previousOtherValue = ref('');

// COMPUTED PROPERTIES (LA SOLUCIÓN AQUÍ)
const validationRules = computed(() => {
    // IMPORTANTE: Extraemos las variables reactivas AQUÍ AFUERA.
    // Esto obliga a Vue a rastrear 'otherValue' como dependencia. Cada vez que
    // cambie el texto, se regenera la función y Vee-Validate se ve forzado a re-validar.
    const currentOtherText = otherValue.value;
    const isRequired = props.validation.required;
    const customErrorText = props.errorText;

    return (val) => {
        // Normalizamos el valor a un array (soporta tanto comportamiento Radio como Checkbox)
        let selections = [];
        if (Array.isArray(val)) {
            selections = val;
        } else if (val !== null && val !== undefined && val !== '') {
            selections = [val];
        }

        // 1. Validar si es requerido de forma general (Si no hay nada seleccionado)
        if (isRequired && selections.length === 0) {
            return customErrorText || translate('label.this_value_is_required') || 'This field is required';
        }

        // 2. Validar que el texto exista si 'other' está seleccionado
        if (selections.includes('other')) {
            if (!currentOtherText || currentOtherText.trim() === '') {
                // Mensaje que quieres mostrar cuando 'Other' está vacío
                return translate('label.please_specify_other') || 'This field is required';
            }
        }

        return true;
    };
});

// Configurar useField (extrayendo setValue para controlar a vee-validate)
const { value, errorMessage, meta, setValue, validate: validateField } = useField(props.name, validationRules, {
    initialValue: props.question.answer || [],
    validateOnValueUpdate: false,
    validateOnMount: false,
});

// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && !!errorMessage.value;
});

// METHODS
const handleCheckboxChange = async (optionValue, isChecked) => {
    // Clonamos array para forzar reactividad
    let newSelections = [...selectedValues.value];

    if (isChecked) {
        if (!newSelections.includes(optionValue)) {
            newSelections.push(optionValue);
        }
    } else {
        newSelections = newSelections.filter(val => val !== optionValue);
    }

    selectedValues.value = newSelections;

    if (optionValue === 'other') {
        if (isChecked) {
            otherValue.value = previousOtherValue.value;
            const finalValue = otherValue.value || '';
            emit('other-selected', { optionId: props.otherId, otherValue: finalValue, isSelected: true });
        } else {
            if (otherValue.value) {
                previousOtherValue.value = otherValue.value;
            }
            otherValue.value = '';
            emit('other-selected', { optionId: props.otherId, otherValue: '', isSelected: false });
        }
    }

    // Le inyectamos manualmente el array a vee-validate
    setValue([...newSelections]);

    emit('update:modelAnswer', newSelections);
    emit('field-change', props.name, newSelections);

    if (props.hasSubmitted) {
        await validateField();
    }
};

// Manejar cambios en el campo "other"
const handleOtherTextChange = async (newText) => {
    otherValue.value = newText;
    previousOtherValue.value = newText;

    if (selectedValues.value.includes('other')) {
        emit('other-selected', {
            optionId: props.otherId,
            otherValue: newText,
            isSelected: true
        });

        // Revalida mientras el usuario escribe si ya hubo intento de submit
        if (props.hasSubmitted) {
            await validateField();
        }
    }
};

// Verificar si una opción está seleccionada
const isOptionSelected = (optionValue) => {
    return selectedValues.value.includes(optionValue);
};

// LYFECYCLE HOOKS
onMounted(() => {
    emit('register-field', props.name, selectedValues.value);
});

// Sincronizar con el valor externo
watch(() => props.question.answer, (newValue) => {
    // Normalizar a Array para que includes('other') siempre funcione
    let valToSet = [];
    if (Array.isArray(newValue)) {
        valToSet = [...newValue];
    } else if (newValue !== null && newValue !== undefined && newValue !== '') {
        valToSet = [newValue];
    }

    selectedValues.value = valToSet;
    setValue(valToSet);
}, { immediate: true });

watch(() => props.question.answerOther, (newValue) => {
    otherValue.value = newValue || '';
}, { immediate: true });

</script>
